import * as ngCore from '@angular/core'
import * as ngRouter from '@angular/router'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as router from '@attain-apps/fe/web/main/lib/router'
import { capitalize } from '@attain-apps/string-utils'
import { TypeAsserter } from '@attain-apps/type-guards'

import * as state from '../../../../../../../state'
import * as modals from '../../modals'
import * as shared from '../shared'

import * as functionalUsage from './functional-usage'
import * as general from './general'
import * as labAssignments from './lab-assignments'
import * as organizationAssignments from './organization-assignments'

/** Space record details page. */
@ngCore.Component({
  imports: [
    ds.input.text.Component,
    ds.layout.widget.Component,
    ds.layout.page.Component,
    ds.layout.page.child.header.Component,
    ds.overlay.centered.Component,
    ds.overlay.attached.Component,
    ds.overlay.confirmation.Component,
    ds.tooltip.Directive,
    organizationAssignments.Component,
    functionalUsage.Component,
    labAssignments.Component,
    general.Component,
    shared.occupants.Component,
    shared.responsiblePersons.Component,
    ds.tag.Component,
    ds.notice.Component,
    modals.recordHistoryLog.Component,
    modals.clusterAssignment.Component,
  ],
  selector: 'atn-space-record-details',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link router.Router} */
  private readonly _router = ngCore.inject(router.Router)
  /** Activated route used to pass relative path in breadcrumb */
  private readonly _activatedRoute = ngCore.inject(ngRouter.ActivatedRoute)

  /** @see {@link state.me.Feature} */
  private readonly _meFeature = ngCore.inject(state.me.Feature)

  /** @see {@link state.space.surveyConfiguration.Feature} */
  private readonly _surveyConfigurationFeature = ngCore.inject(
    state.space.surveyConfiguration.Feature,
  )

  /** @see {@link state.space.userAccess.surveyAdmins.Collection} */
  private readonly _surveyAdminsEc = ngCore.inject(
    state.space.userAccess.surveyAdmins.Collection,
  )

  /** @see {@link state.space.full.records.Collection} */
  public readonly recordsEc = ngCore.inject(state.space.full.records.Collection)

  /** @see {@link state.space.full.clusters.Collection} */
  private readonly _clustersEc = ngCore.inject(
    state.space.full.clusters.Collection,
  )

  /** @see {@link state.space.full.recordOccupants.Collection} */
  public readonly occupantsEc = ngCore.inject(
    state.space.full.recordOccupants.Collection,
  )

  /** @see {@link state.space.full.responsiblePersons.recordResponsiblePersons.Collection} */
  public readonly responsiblePersonsEc = ngCore.inject(
    state.space.full.responsiblePersons.recordResponsiblePersons.Collection,
  )

  /** @see {@link state.space.full.warnings.plugin.Plugin} */
  public readonly recordWarningsPlugin = this.recordsEc.plugins
    .lookup(state.space.full.warnings.plugin.Plugin)
    .get()

  /** @see {@link state.space.full.records.plugins.clusterAssignment.Plugin} */
  public readonly clusterAssignmentPlugin = this.recordsEc.plugins
    .lookup(state.space.full.records.plugins.clusterAssignment.Plugin)
    .get()

  /** @see {@link state.space.full.records.plugins.surveyStatus.Plugin} */
  public readonly surveyStatusPlugin = this.recordsEc.plugins
    .lookup(state.space.full.records.plugins.surveyStatus.Plugin)
    .get()

  /** Exposes {@link Operation} to the template */
  public readonly OperationEnum =
    state.space.full.records.plugins.surveyStatus.Operation

  /** @see {@link state.space.full.employee.Collection} */
  public readonly employeesEc = ngCore.inject(
    state.space.full.employee.Collection,
  )

  /** @see {@link state.space.roomTypes.Collection} */
  private readonly _roomTypesEc = ngCore.inject(
    state.space.roomTypes.Collection,
  )

  /**
   * Controls whether record history log overlay is currently visible
   */
  public readonly presentRecordHistoryLogSg = ngCore.signal(false)

  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)

  /** Selected record */
  private readonly _recordSg = ngCore.computed(() =>
    this.recordsEc.plugins.currentEntityPlugin.feature.selectors
      .getEntity()
      .asSignal(),
  )

  /** Selected record id */
  private readonly _recordIdSg = ngCore.computed(
    (record = this._recordSg()) => record?.id,
  )

  /** Selected record name */
  public readonly recordNameSg = ngCore.computed(
    (record = this._recordSg()) => record?.name,
  )

  /** Selected room type */
  private readonly _roomTypeSg = ngCore.computed(
    (
      record = this._recordSg(),
      roomTypes = this._roomTypesEc.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
    ) => roomTypes.find(rt => rt.id === record?.roomTypeId),
  )

  /** Whether the current record is a Lab Service room */
  public readonly isLabServiceSg = ngCore.computed(
    (roomType = this._roomTypeSg()) =>
      roomType?.category === ents.extra.space.RoomType.Category.LabService,
  )

  /** Selected survey status */
  public readonly surveyStatusSg = ngCore.computed(
    (record = this._recordSg()) => record?.surveyStatus,
  )

  /** Whether the current record has already been signed off */
  public readonly isSignedOffSg = ngCore.computed(
    (surveyStatus = this.surveyStatusSg()) =>
      surveyStatus === ents.extra.space.SurveyStatus.SignedOff,
  )

  /**
   * Whether the current user is allowed to unlock a signed-off room: either
   * Survey Configuration allows any user to unlock directly, or the current
   * user is a Platform Admin, Tenant Admin, or Survey Admin.
   */
  public readonly canUnlockSg = ngCore.computed(
    (
      config = this._surveyConfigurationFeature.selectors.config.asSignal(),
      canManageUsers = this._meFeature.contexts.selectors.currentUserCanManageUsers.asSignal(),
      currentUserId = this._meFeature.selectors.currentUserId.asSignal(),
      surveyAdminUserIds = new Set(
        this._surveyAdminsEc.plugins.currentListPlugin.feature.selectors.entities
          .asSignal()
          .map(({ userId }) => userId),
      ),
    ) =>
      config?.tools.allowUsersToUnlockRoomsDirectly === true ||
      !!(
        currentUserId &&
        (canManageUsers || surveyAdminUserIds.has(currentUserId))
      ),
  )

  /** Tooltip explaining why the sign off button is disabled, if applicable */
  public readonly signOffTooltipSg = ngCore.computed(
    (
      hasWarnings = this.recordWarningsPlugin.selectors.hasWarnings.asSignal(),
    ) =>
      hasWarnings
        ? 'Resolve all warnings on this page before signing off.'
        : undefined,
  )

  /**
   * Tooltip explaining why the cluster assignment button is disabled, if
   * applicable.
   */
  public readonly clusterAssignmentTooltipSg = ngCore.computed(
    (isSignedOff = this.isSignedOffSg()) =>
      isSignedOff
        ? 'Signed off rooms cannot be assigned to a cluster.'
        : undefined,
  )

  /** Cluster the current record is assigned to, if any. */
  public readonly clusterSg = ngCore.computed(
    (
      record = this._recordSg(),
      clustersLookup = this._clustersEc.plugins.cachePlugin.feature.selectors.lookup.asSignal(),
    ) => (record?.clusterId ? clustersLookup[record.clusterId] : undefined),
  )

  /** Name of the cluster the current record is assigned to, if any. */
  public readonly clusterNameSg = ngCore.computed(
    (cluster = this.clusterSg()) => cluster?.name,
  )

  /** Selected record ancestors */
  public readonly recordAncestorsSg = ngCore.computed(
    (
      record = this._recordSg(),
      schema = this._schemaFeature.selectors.schema.asSignal(),
    ) =>
      (record &&
        schema.getAncestorsOf(record?.entityId).map(ancestor => ({
          name: ancestor.name,
          value:
            ancestor.id === record.entityId
              ? record.name
              : record.ancestors[ancestor.id]?.name,
        }))) ??
      [],
  )

  /** Breadcrumbs, only the first element in array should be clickable */
  public readonly breadcrumbsSg = ngCore.computed<
    ds.layout.page.child.header.models.Breadcrumb[]
  >((ancestors = this.recordAncestorsSg()) => [
    // We want only the institution to be clickable so we pass it the url to
    // records list page and leave the rest of the ancestors without url, so
    // they are not clickable
    {
      name: 'Institution',
      url: this._router.ngRouter.createUrlTree(
        [`../${ents.extra.prebuilt.Page.Id.SpaceRecordsList}`],
        {
          queryParamsHandling: 'preserve',
          relativeTo: this._activatedRoute,
        },
      ),
    },
    // For the rest of the ancestors we just want to show their names in
    // breadcrumbs, so we don't pass them the url, so they are not clickable
    ...ancestors.map(ancestor => ({
      name: ancestor.value,
    })),
    // In case there are no ancestors, we still want to show the record name
    // in breadcrumbs
    {
      name: this.recordNameSg() ?? 'Record',
    },
  ])

  /** Navigate to the cluster details page for the assigned cluster. */
  public clusterDetailsClickHandler(): void {
    const cluster = this.clusterSg()
    if (!cluster) {
      return
    }

    const source = 'RecordDetails.ClusterNotice.Clicked'

    this._clustersEc.plugins.currentEntityPlugin.storage.actions.putInIdLookup
      .from(source)
      .dispatch({ value: cluster.id })

    const url = this._meFeature.contexts.selectors
      .getUrlForLink({
        goto: { navigationId: ents.extra.prebuilt.Page.Id.SpaceCluster },
      })
      .asSignal()

    if (url) {
      this._router.actions.navigate.from(source).dispatch({ url })
    }
  }

  /** Initiate cluster assignment. */
  public clusterAssignmentClickHandler(): void {
    this.clusterAssignmentPlugin.actions.initiate
      .from('RecordDetails.ClusterAssignment.Icon.Clicked')
      .dispatch({
        recordIds: [
          TypeAsserter.validate(
            this._recordIdSg(),
            TypeAsserter.assertIsNotNullish,
          ),
        ],
      })
  }

  /** Opens the confirmation dialog for the given survey lock operation. */
  public initiateClickHandler(
    operation: state.space.full.records.plugins.surveyStatus.Operation,
  ): void {
    this.surveyStatusPlugin.actions.initiate
      .from(`RecordDetails.${capitalize(operation)}.Icon.Clicked`)
      .dispatch({ operation })
  }

  /** Confirms and submits the given survey lock operation. */
  public confirmClickHandler(
    operation: state.space.full.records.plugins.surveyStatus.Operation,
  ): void {
    this.surveyStatusPlugin.actions.submit
      .from(
        `RecordDetails.${capitalize(operation)}.Confirmation.Footer.Buttons.Confirm.Clicked`,
      )
      .dispatch({ operation })
  }

  /** Cancels the confirmation dialog for the given survey lock operation. */
  public cancelClickHandler(
    operation: state.space.full.records.plugins.surveyStatus.Operation,
  ): void {
    this.surveyStatusPlugin.actions.cancel
      .from(
        `RecordDetails.${capitalize(operation)}.Confirmation.Footer.Buttons.Cancel.Clicked`,
      )
      .dispatch({ operation })
  }
}
